import React from 'react';
import { Search, Filter, SortAsc, SortDesc, Globe, Lock, BookOpen } from 'lucide-react';
import { useLang } from '../../contexts/LanguageContext';

const TCG_TYPES = [
  { key: '', labelKey: 'collectionsShared.all', fallback: 'All' },
  { key: 'pokemon', fallback: 'Pokémon' },
  { key: 'magic', fallback: 'Magic' },
  { key: 'yugioh', fallback: 'Yu-Gi-Oh!' },
  { key: 'onepiece', fallback: 'One Piece' },
  { key: 'riftbound', fallback: 'Riftbound' },
  { key: 'mixed', labelKey: 'collectionsShared.mixed', fallback: 'Mixed' },
];

export function getTcgBadgeColor(tcgType) {
  switch (tcgType) {
    case 'pokemon': return 'bg-[#D4A84B]/20 text-[#D4A84B] border-[#D4A84B]/30';
    case 'magic': return 'bg-[#0D5E4A]/20 text-[#2D8B70] border-[#0D5E4A]/30';
    case 'yugioh': return 'bg-[#7B3F00]/20 text-[#C9874B] border-[#7B3F00]/30';
    case 'onepiece': return 'bg-[#C45B4A]/20 text-[#F87171] border-[#C45B4A]/30';
    case 'riftbound': return 'bg-[#0BC4E2]/20 text-[#0BC4E2] border-[#0BC4E2]/30';
    default: return 'bg-[#C9A962]/20 text-[#C9A962] border-[#C9A962]/30';
  }
}

export function PublicFilters({ 
  publicSearch, 
  setPublicSearch, 
  publicTcgFilter, 
  setPublicTcgFilter, 
  publicSortOrder, 
  setPublicSortOrder,
  resultCount
}) {
  const { t } = useLang();
  return (
    <div className="bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl p-4 mb-6" data-testid="public-filters">
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8B5AE]" />
          <input 
            value={publicSearch} 
            onChange={e => setPublicSearch(e.target.value)}
            placeholder={t('collectionsShared.search_collection_or_user')}
            className="w-full bg-[#0A1612] border border-[#C9A962]/20 rounded-xl pl-10 pr-4 py-2.5 text-[#F5F0E6] text-sm outline-none focus:border-[#C9A962] transition-colors placeholder:text-[#A8B5AE]/50"
            data-testid="public-search-input"
          />
        </div>
        
        {/* TCG Filter */}
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-[#A8B5AE]" />
          <select 
            value={publicTcgFilter} 
            onChange={e => setPublicTcgFilter(e.target.value)}
            className="bg-[#0A1612] border border-[#C9A962]/20 rounded-xl px-4 py-2.5 text-sm text-[#F5F0E6] outline-none focus:border-[#C9A962] transition-colors"
            data-testid="tcg-filter-select"
          >
            {TCG_TYPES.map(type => (
              <option key={type.key} value={type.key}>
                {type.labelKey ? t(type.labelKey) : type.fallback}
              </option>
            ))}
          </select>
        </div>
        
        {/* Sort */}
        <div className="flex items-center gap-2">
          {publicSortOrder.includes('asc') ? <SortAsc size={14} className="text-[#A8B5AE]" /> : <SortDesc size={14} className="text-[#A8B5AE]" />}
          <select 
            value={publicSortOrder} 
            onChange={e => setPublicSortOrder(e.target.value)}
            className="bg-[#0A1612] border border-[#C9A962]/20 rounded-xl px-4 py-2.5 text-sm text-[#F5F0E6] outline-none focus:border-[#C9A962] transition-colors"
            data-testid="sort-select"
          >
            <option value="name_asc">{t('collectionsShared.sort_name_asc')}</option>
            <option value="name_desc">{t('collectionsShared.sort_name_desc')}</option>
            <option value="cards_desc">{t('collectionsShared.sort_cards_desc')}</option>
            <option value="cards_asc">{t('collectionsShared.sort_cards_asc')}</option>
          </select>
        </div>
      </div>
      
      {/* Results count */}
      <div className="mt-3 text-xs text-[#A8B5AE]">
        {resultCount} {t('collectionsShared.collections_found')}
        {publicSearch && <span className="text-[#C9A962]"> "{publicSearch}"</span>}
      </div>
    </div>
  );
}

export function CollectionList({ 
  collections, 
  selectedCollection, 
  onSelect, 
  tab, 
  user, 
  t 
}) {
  if (!user && tab === 'my') {
    return (
      <div className="text-center py-12 text-[#A8B5AE]">
        <Lock size={32} className="mx-auto mb-3 opacity-40" />
        <p className="text-sm">{t('collectionsShared.login_to_view')}</p>
      </div>
    );
  }

  if (collections.length === 0) {
    return (
      <div className="text-center py-12 text-[#A8B5AE]">
        <BookOpen size={32} className="mx-auto mb-3 opacity-40" />
        <p className="text-sm">{t('collectionsShared.no_collections')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
      {collections.map(col => (
        <button 
          key={col.collection_id} 
          onClick={() => onSelect(col)}
          className={`w-full text-left p-4 rounded-xl border transition-all ${
            selectedCollection?.collection_id === col.collection_id 
              ? 'border-[#C9A962] bg-[#C9A962]/10' 
              : 'border-[#C9A962]/10 bg-[#0F1F1A] hover:border-[#C9A962]/30'
          }`}
          data-testid={`collection-${col.collection_id}`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#F5F0E6] font-semibold text-sm truncate flex-1">{col.name}</span>
            <div className="flex items-center gap-2 ml-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${getTcgBadgeColor(col.tcg_type)}`}>
                {col.tcg_type === 'mixed' ? t('collectionsShared.mixed') : col.tcg_type}
              </span>
              {col.is_public ? <Globe size={13} className="text-[#2D8B70]" /> : <Lock size={13} className="text-[#A8B5AE]" />}
            </div>
          </div>
          <p className="text-[#A8B5AE] text-xs">{col.cards?.length || 0} {t('collections.cards')}</p>
          {col.user_name && tab === 'public' && (
            <p className="text-[#C9A962] text-xs mt-1 flex items-center gap-1">
              <span className="w-4 h-4 rounded-full bg-[#C9A962]/20 flex items-center justify-center text-[8px] font-bold">
                {col.user_name[0]?.toUpperCase()}
              </span>
              @{col.user_name}
            </p>
          )}
        </button>
      ))}
    </div>
  );
}
